/**
 * DealCloseDateEditDialog - Modal para editar a data de conclusão (actual_close_date)
 * de um negócio já ganho ou perdido.
 *
 * @version 1.0.0
 * RELEASE: 2026-05-27 - Versão inicial. Date picker + horário, com validação
 *          de data futura no frontend e submissão via useUpdateDealCloseDate.
 */
import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarIcon, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useUpdateDealCloseDate } from '@/hooks/useUpdateDealCloseDate';
import { toDateOnlyISO, parseDateOnly } from '@/lib/utils/date-only';



interface DealCloseDateEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  /** Data atual em ISO; usada como valor inicial */
  currentValue: string | null;
}

export function DealCloseDateEditDialog({
  open,
  onOpenChange,
  dealId,
  currentValue,
}: DealCloseDateEditDialogProps) {
  // Usa parseDateOnly para evitar shift de fuso quando currentValue é DATE puro ("YYYY-MM-DD")
  const initialDate = useMemo(
    () => parseDateOnly(currentValue) ?? new Date(),
    [currentValue]
  );


  const [date, setDate] = useState<Date>(initialDate);

  // Reseta estado quando reabre
  useEffect(() => {
    if (open) {
      setDate(initialDate);
    }
  }, [open, initialDate]);

  const mutation = useUpdateDealCloseDate();

  // Normaliza a data para meia-noite local (sem hora/minuto)
  const normalizedDate = useMemo(() => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [date]);

  // Comparação "só dia" para bloquear datas futuras
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const isFuture = normalizedDate.getTime() > today.getTime();

  const handleSave = async () => {
    if (isFuture) return;
    try {
      await mutation.mutateAsync({
        dealId,
        actualCloseDate: toDateOnlyISO(date),
      });
      onOpenChange(false);
    } catch {
      // toast já exibido no hook
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Alterar data de conclusão</DialogTitle>
          <DialogDescription>
            Atualize a data em que este negócio foi concluído. Não altera o status.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Data */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Data</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !date && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date
                    ? format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
                    : 'Selecione a data'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => d && setDate(d)}
                  disabled={(d) => d > new Date()}
                  initialFocus
                  className={cn('p-3 pointer-events-auto')}
                  locale={ptBR}
                />
              </PopoverContent>
            </Popover>
          </div>

          {isFuture && (
            <p className="text-xs text-destructive">
              A data informada não pode ser futura.
            </p>
          )}
        </div>


        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={mutation.isPending || isFuture} className="gap-1.5">
            {mutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
